/**
 * web/src/screens/Notebook/giftflow/step2.tsx — Step 2, Questions (and, in
 * defaults mode, the Gift-defaults editor): three skippable chip groups
 * pre-filled from the Gift defaults, the free-text box, the draw-on line and
 * the primary action (Find ideas / Save defaults) (card NYJEJRCBfBGQ).
 */
import { useEffect, useState } from 'react';
import type { GiftFlowState } from './hooks';
import { prefillDraft } from './hooks';
import { BUDGET_CHIPS, KIND_CHIPS, VIBE_CHIPS, fmtDate, type BriefDraft } from './types';

export function StepQuestions({ g, defaultsMode }: { g: GiftFlowState; defaultsMode: boolean }) {
  const [draft, setDraft] = useState<BriefDraft>(() => prefillDraft(g));

  // Re-prefill when the underlying brief/defaults land (e.g. opening the
  // defaults editor, or returning from an edit).
  useEffect(() => {
    setDraft(prefillDraft(g));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [g.brief?.briefId, g.defaultsBrief?.briefId, defaultsMode]);

  const set = (patch: Partial<BriefDraft>) => setDraft((d) => ({ ...d, ...patch }));

  const budgetChipFor = (min: number | null, max: number | null) =>
    BUDGET_CHIPS.findIndex((c) => c.min === min && c.max === max);

  const onPrimary = () => {
    const d: BriefDraft = {
      ...draft,
      // No budget chip chosen reads as "No limit" — skipping is allowed.
      budgetMin: draft.budgetMin === undefined ? null : draft.budgetMin,
    };
    if (defaultsMode) void g.saveDefaults(d);
    else void g.findIdeas(d);
  };

  return (
    <section className="gf-step" data-testid={defaultsMode ? 'gift-defaults' : 'gift-step-2'}>
      <h4 className="gf-heading" data-testid="gift-step2-heading">
        {defaultsMode ? 'Gift defaults' : 'A few quick questions'}
      </h4>
      <p className="gf-muted" data-testid="gift-step2-sub">
        {defaultsMode
          ? 'These fill in every new search. Only you can see them.'
          : `Skip any: ${g.active?.occasionType ?? ''}${g.active?.occasionDate ? ` · ${fmtDate(g.active.occasionDate)}` : ''}`}
      </p>

      <p className="gf-label">Kind of gift</p>
      <div className="gf-chips" role="group" aria-label="Kind of gift" data-testid="gift-chips-kind">
        {KIND_CHIPS.map((c) => (
          <button
            key={c.label}
            type="button"
            className={`gf-chip${draft.kind === c.value ? ' is-on' : ''}`}
            data-testid={`gift-kind-${c.value ?? 'surprise'}`}
            aria-pressed={draft.kind === c.value}
            onClick={() => set({ kind: draft.kind === c.value ? null : c.value })}
          >
            {c.label}
          </button>
        ))}
      </div>

      <p className="gf-label">Budget</p>
      <div className="gf-chips" role="group" aria-label="Budget" data-testid="gift-chips-budget">
        {BUDGET_CHIPS.map((c, i) => {
          const isOn = budgetChipFor(draft.budgetMin, draft.budgetMax) === i;
          return (
            <button
              key={c.label}
              type="button"
              className={`gf-chip${isOn ? ' is-on' : ''}`}
              data-testid={`gift-budget-${i}`}
              aria-pressed={isOn}
              onClick={() => set(isOn ? { budgetMin: null, budgetMax: null } : { budgetMin: c.min, budgetMax: c.max })}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      <p className="gf-label">Vibe</p>
      <div className="gf-chips" role="group" aria-label="Vibe" data-testid="gift-chips-vibe">
        {VIBE_CHIPS.map((c) => (
          <button
            key={c.value}
            type="button"
            className={`gf-chip${draft.vibe === c.value ? ' is-on' : ''}`}
            data-testid={`gift-vibe-${c.value}`}
            aria-pressed={draft.vibe === c.value}
            onClick={() => set({ vibe: draft.vibe === c.value ? null : c.value })}
          >
            {c.label}
          </button>
        ))}
      </div>

      <p className="gf-label">Anything else? Asks, hints, things to avoid</p>
      <textarea
        className="gf-free-text"
        data-testid="gift-free-text"
        value={draft.freeText}
        placeholder="She wants a better coffee setup. Nothing with candles."
        rows={3}
        onChange={(e) => set({ freeText: e.target.value })}
      />

      <p className="gf-muted" data-testid="gift-step2-drawon">
        I&rsquo;ll draw on: {g.partnerName}&rsquo;s interests, {g.partnerName}&rsquo;s private list. Only you see this search.
      </p>

      <button
        type="button"
        className="pear-btn pear-btn-mustard gf-continue"
        data-testid={defaultsMode ? 'gift-save-defaults' : 'gift-find-ideas'}
        disabled={g.busy}
        onClick={onPrimary}
      >
        {defaultsMode ? 'Save defaults' : 'Find ideas'}
      </button>
    </section>
  );
}
